import React from 'react';
import { motion, useMotionTemplate, useMotionValue, useReducedMotion, useSpring } from 'framer-motion';
import { cn } from '@/lib/utils';

// ── Shared marketing design system ─────────────────────────────────────────
// Source of truth for the ReturnCatalyst premium marketing surface. The home
// page (LandingPage.tsx) and every page under pages/marketing/* import from
// here so the logged-out site shares one design language: Georgia display
// serif headings (`landing-display-lg`), Inter body (`font-sans-swiss`),
// sage-edged tactile panels on near-black, rounded-[2px].

export const ease = [0.16, 1, 0.3, 1] as [number, number, number, number];

export const fadeIn = {
    hidden: { opacity: 0, y: 32 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease } },
};

export const fadeInFast = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease } },
};

export const stagger = (d = 0.08) => ({
    hidden: {},
    visible: { transition: { staggerChildren: d, delayChildren: 0.05 } },
});

export const slideL = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease } },
};

export const slideR = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease } },
};

export const vp = { once: true, amount: 0.15 } as const;

export const tactileShellBackgrounds = {
    default: 'linear-gradient(180deg, rgba(141,166,150,0.3) 0%, rgba(255,255,255,0.04) 52%, rgba(220,229,223,0.16) 100%)',
    blue: 'linear-gradient(180deg, rgba(67,118,82,0.52) 0%, rgba(255,255,255,0.05) 48%, rgba(141,166,150,0.26) 100%)',
    warm: 'linear-gradient(180deg, rgba(226,222,207,0.3) 0%, rgba(141,166,150,0.14) 52%, rgba(255,255,255,0.1) 100%)',
    success: 'linear-gradient(180deg, rgba(141,166,150,0.42) 0%, rgba(255,255,255,0.05) 48%, rgba(220,229,223,0.16) 100%)',
    danger: 'linear-gradient(180deg, rgba(220,229,223,0.2) 0%, rgba(145,109,102,0.2) 52%, rgba(255,255,255,0.1) 100%)',
} as const;

export type TactileTone = keyof typeof tactileShellBackgrounds;

export function TactilePanel({
    children,
    className,
    innerClassName,
    tone = 'default',
}: {
    children: React.ReactNode;
    className?: string;
    innerClassName?: string;
    tone?: TactileTone;
}) {
    const reducedMotion = useReducedMotion();
    const lightX = useMotionValue(0);
    const lightY = useMotionValue(0);
    const lightOpacity = useSpring(0, { stiffness: 160, damping: 28 });
    const light = useMotionTemplate`radial-gradient(360px circle at ${lightX}px ${lightY}px, rgba(169,194,177,0.14), transparent 75%)`;

    return (
        <div
            className={cn('marketing-tactile-panel rounded-[2px] p-px shadow-[0_0_40px_rgba(255,255,255,0.02)]', className)}
            style={{ background: tactileShellBackgrounds[tone] }}
            onPointerMove={(event) => {
                if (reducedMotion || event.pointerType !== 'mouse') return;
                const bounds = event.currentTarget.getBoundingClientRect();
                lightX.set(event.clientX - bounds.left);
                lightY.set(event.clientY - bounds.top);
                lightOpacity.set(1);
            }}
            onPointerLeave={() => lightOpacity.set(0)}
        >
            <div
                className={cn(
                    'relative h-full overflow-hidden rounded-[1px] border border-white/15 bg-black/[0.92] backdrop-blur-[24px]',
                    innerClassName,
                )}
                style={{
                    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.04), 0 0 0 1px rgba(255,255,255,0.02), 0 0 40px rgba(255,255,255,0.02)',
                }}
            >
                <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0.02)_38%,rgba(0,0,0,0)_100%)] opacity-90" />
                {!reducedMotion && <motion.div aria-hidden="true" className="marketing-tactile-light pointer-events-none absolute inset-0" style={{ background: light, opacity: lightOpacity }} />}
                <div className="pointer-events-none absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-[#8DA696]/80 to-transparent" />
                <div className="relative z-10 h-full">{children}</div>
            </div>
        </div>
    );
}

export function TactileCard({
    icon: Icon,
    eyebrow,
    title,
    description,
    footer,
    className,
    motionIndex = 0,
    tone = 'default',
}: {
    icon?: React.ElementType;
    eyebrow?: string;
    title: string;
    description: string;
    footer?: React.ReactNode;
    className?: string;
    motionIndex?: number;
    tone?: TactileTone;
}) {
    const prefersReducedMotion = useReducedMotion();

    return (
        <motion.div
            className={cn('group h-full', className)}
            initial={prefersReducedMotion ? false : { opacity: 0, y: 10, filter: 'blur(4px)' }}
            whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0, filter: 'blur(0px)' }}
            whileHover={prefersReducedMotion ? undefined : { filter: 'brightness(1.08)' }}
            whileTap={prefersReducedMotion ? undefined : { scale: 0.995 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{
                duration: 0.15,
                delay: Math.min(motionIndex * 0.025, 0.12),
                ease: [0.4, 0, 0.2, 1],
            }}
        >
            <TactilePanel className="h-full" tone={tone}>
                <div className="flex h-full flex-col gap-5 p-5 md:p-6">
                    <div className="flex items-start justify-between gap-4">
                        <div className="space-y-4">
                            {eyebrow ? (
                                <div className="landing-label-md text-[#8DA696] transition-colors duration-150 ease-out group-hover:text-white">
                                    {eyebrow}
                                </div>
                            ) : null}
                            <div className="flex items-center gap-3">
                                {Icon ? (
                                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[2px] border border-white/20 bg-black text-[#8DA696] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03)] transition-colors duration-150 ease-out group-hover:border-[#8DA696]/70 group-hover:text-white">
                                        <Icon className="h-4 w-4" />
                                    </div>
                                ) : null}
                                <h3 className="font-sans-swiss text-[20px] font-medium leading-5 tracking-normal text-white transition-colors duration-150 ease-out group-hover:text-[#C9D7C8]">
                                    {title}
                                </h3>
                            </div>
                        </div>
                    </div>
                    <p className="landing-body-md text-[#CBD5E1]/[0.84]">{description}</p>
                    {footer ? (
                        <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-white/10 pt-4 text-[11px] font-medium uppercase tracking-[0.18em] text-white/[0.58]">
                            {footer}
                        </div>
                    ) : null}
                </div>
            </TactilePanel>
        </motion.div>
    );
}

// ── Shared section primitives ──────────────────────────────────────────────

/** Uppercase tracked eyebrow used above every section heading. */
export function SectionEyebrow({ children, className }: { children: React.ReactNode; className?: string }) {
    return <p className={cn('landing-label-md mb-5 text-zinc-600', className)}>{children}</p>;
}

/** Standard scroll-reveal wrapper for a marketing section's inner container. */
export function RevealContainer({
    children,
    className,
    staggerDelay = 0.12,
}: {
    children: React.ReactNode;
    className?: string;
    staggerDelay?: number;
}) {
    const prefersReducedMotion = useReducedMotion();

    return (
        <motion.div
            className={className}
            variants={stagger(staggerDelay)}
            initial={prefersReducedMotion ? false : 'hidden'}
            whileInView={prefersReducedMotion ? undefined : 'visible'}
            viewport={vp}
        >
            {children}
        </motion.div>
    );
}
